import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { FundFitToolUsageByTool } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface FundFitToolUsageByToolChartProps {
  data: FundFitToolUsageByTool[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: {
      name: string;
      value: number;
      percentage: number;
      cost: number;
      users: number;
      color: string;
    };
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium' style={{ color: item.payload.color }}>
        {item.name}
      </p>
      <p className='text-sm'>
        {formatNumber(item.value)} calls ({item.payload.percentage}%)
      </p>
      <p className='text-sm'>Cost: {formatCurrency(item.payload.cost)}</p>
      <p className='text-sm text-muted-foreground'>Users: {item.payload.users}</p>
    </div>
  );
};

const FundFitToolUsageByToolChart = ({ data }: FundFitToolUsageByToolChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Usage by Tool</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[220px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item, index) => ({
    name: item.tool_name,
    value: item.tool_count,
    percentage: parseFloat(item.percentage_of_total),
    cost: parseFloat(item.total_tool_usd),
    users: item.unique_users,
    color: CHART_COLORS.pie[index % CHART_COLORS.pie.length],
  }));

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Usage by Tool</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col md:flex-row gap-4 items-start'>
          <div className='flex-shrink-0'>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='50%'
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className='flex-1 space-y-3'>
            {chartData.map((item, index) => (
              <div key={index} className='border-b pb-2 last:border-b-0'>
                <div className='flex items-center gap-2 text-sm'>
                  <div
                    className='w-3 h-3 rounded-sm flex-shrink-0'
                    style={{ backgroundColor: item.color }}
                  />
                  <span className='flex-1 font-medium truncate'>{item.name}</span>
                  <span className='text-muted-foreground'>
                    {formatNumber(item.value)} ({item.percentage}%)
                  </span>
                </div>
                <div className='ml-5 mt-1 text-xs text-muted-foreground'>
                  Cost: {formatCurrency(item.cost)} | Users: {item.users}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FundFitToolUsageByToolChart;
